<template>
  <div class="payment-due-list">
    <b-card>
      <!-- Filters follow the same responsive layout used by the other management pages. -->
      <b-row class="align-items-end mb-2">
        <b-col
          cols="12"
          md="3"
        >
          <b-form-group
            label="Batch"
            label-for="due-list-batch"
          >
            <b-form-select
              id="due-list-batch"
              v-model="filters.batch"
              :options="batchOptions"
            />
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          md="3"
        >
          <b-form-group
            label="Intake"
            label-for="due-list-intake"
          >
            <b-form-select
              id="due-list-intake"
              v-model="filters.intake"
              :options="intakeOptions"
            />
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          md="3"
        >
          <b-form-group
            label="Payment Month"
            label-for="due-list-current-month"
          >
            <b-form-select
              id="due-list-current-month"
              v-model="filters.currentMonth"
              :options="currentMonthOptions"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="3"
        >
          <b-form-group
            label="Name"
            label-for="due-list-name"
          >
            <b-form-input
              id="due-list-name"
              v-model.trim="filters.name"
              placeholder="Search by name"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Keep wide payment columns scrollable on smaller screens. -->
      <b-table
        responsive
        striped
        hover
        show-empty
        :busy="loading"
        :items="filteredDueList"
        :fields="fields"
        empty-text="No payment dues found"
      >
        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner class="align-middle mr-50" />
            <strong>Loading payment dues...</strong>
          </div>
        </template>

        <!-- Amounts are calculated by the backend and formatted for display only. -->
        <template #cell(paid_amount)="data">
          {{ getPrice(data.value) }}
        </template>

        <template #cell(due_amount)="data">
          {{ getPrice(data.value) }}
        </template>

        <template #cell(balance_amount)="data">
          {{ getPrice(data.value) }}
        </template>

        <!-- A missing month means there is no matching active meeting. -->
        <template #cell(current_month)="data">
          {{ data.value || 'N/A' }}
        </template>

        <!-- A missing value means no flexible-payment deadline is currently assigned. -->
        <template #cell(due_date)="data">
          {{ data.value || 'N/A' }}
        </template>

        <template #custom-foot>
          <b-tr class="payment-total-row">
            <b-th colspan="4">
              Total
            </b-th>
            <b-th>{{ getPrice(filteredDataTotals.paidAmount) }}</b-th>
            <b-th>{{ getPrice(filteredDataTotals.dueAmount) }}</b-th>
            <b-th colspan="3" />
          </b-tr>
        </template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCol,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BRow,
  BSpinner,
  BTable,
  BTh,
  BTr,
} from 'bootstrap-vue'
import paymentApi from '@/Api/Modules/payment'

export default {
  name: 'PaymentDueList',
  components: {
    BCard,
    BCol,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BRow,
    BSpinner,
    BTable,
    BTh,
    BTr,
  },
  data() {
    return {
      loading: false,
      dueListRequestId: 0,
      dueList: [],
      filters: {
        batch: null,
        intake: null,
        currentMonth: null,
        name: '',
      },
      fields: [
        // These keys map directly to the consolidated backend response.
        { key: 'batch', label: 'Batch', sortable: true },
        { key: 'intake', label: 'Intake', sortable: true },
        { key: 'name', label: 'Name', sortable: true },
        { key: 'current_month', label: 'Payment Month', sortable: true },
        { key: 'paid_amount', label: 'Paid Amount', sortable: true },
        { key: 'due_amount', label: 'Due Amount', sortable: true },
        { key: 'balance_amount', label: 'Balance Amount', sortable: true },
        // Display the stored due date as returned by the backend.
        { key: 'due_date', label: 'Due Date', sortable: true },
        { key: 'phone', label: 'Phone No.', sortable: true },
      ],
    }
  },
  computed: {
    // Build the batch filter choices from the full payment due list.
    batchOptions() {
      return this.createFilterOptions('batch', 'All Batches')
    },
    // Build the intake filter choices from the full payment due list.
    intakeOptions() {
      return this.createFilterOptions('intake', 'All Intakes')
    },
    currentMonthOptions() {
      return [
        { value: null, text: 'Current Meeting Month' },
        ...Array.from({ length: 6 }, (_, index) => ({
          value: index + 1,
          text: `Month ${index + 1}`,
        })),
      ]
    },
    // Apply display-only filters without changing the backend visibility rules.
    filteredDueList() {
      const searchName = this.filters.name.toLowerCase()

      return this.dueList.filter(item => {
        const matchesBatch = !this.filters.batch || item.batch === this.filters.batch
        const matchesIntake = !this.filters.intake || item.intake === this.filters.intake
        const matchesName = !searchName || (item.name || '').toLowerCase().includes(searchName)

        return matchesBatch && matchesIntake && matchesName
      })
    },
    // Calculate paid and due totals from the same rows currently shown in the table.
    filteredDataTotals() {
      return this.filteredDueList.reduce((totals, item) => ({
        paidAmount: totals.paidAmount + this.toAmount(item.paid_amount),
        dueAmount: totals.dueAmount + this.toAmount(item.due_amount),
      }), {
        paidAmount: 0,
        dueAmount: 0,
      })
    },
  },
  watch: {
    'filters.currentMonth': 'loadDueList',
  },
  // Load the consolidated payment due list when the page opens.
  created() {
    this.loadDueList()
  },
  methods: {
    // Create a sorted list of unique options for a payment due filter.
    createFilterOptions(key, emptyLabel) {
      const values = this.dueList
        .map(item => item[key])
        .filter(Boolean)
      const uniqueValues = [...new Set(values)].sort()

      return [
        { value: null, text: emptyLabel },
        ...uniqueValues.map(value => ({ value, text: value })),
      ]
    },
    // Format a numeric amount using the payment list currency display.
    getPrice(value) {
      return `Rs. ${Number(value || 0).toFixed(2)}`
    },
    // Convert an API amount to a valid number and default invalid values to zero.
    toAmount(value) {
      const amount = Number(value)

      return Number.isFinite(amount) ? amount : 0
    },
    // Fetch and store the consolidated payment due list from the backend.
    async loadDueList() {
      this.dueListRequestId += 1
      const requestId = this.dueListRequestId
      this.loading = true
      this.dueList = []

      try {
        // The backend calculates amounts and applies the month/balance visibility rules.
        const response = await paymentApi.getPaymentDueList({
          month: this.filters.currentMonth,
        })
        if (requestId !== this.dueListRequestId) return

        // Keep this page responsible only for presentation and local search filters.
        this.dueList = response.data.data || []
      } catch (error) {
        if (requestId !== this.dueListRequestId) return
        this.dueList = []
        this.$toast.error('Unable to load the payment due list')
      } finally {
        if (requestId === this.dueListRequestId) this.loading = false
      }
    },
  },
}
</script>
